import type {
  Connection,
  ConnectionType,
  Drawing,
  FloorKind,
  Lang,
  Preparation,
  ProductInstance,
  ProductVariant,
  RoomLight,
  StripPos,
  TextI18n,
} from '../shared/types';
import {
  catalogueLightProduct,
  coveGeometry,
  defaultLightProductId,
  lightByTheMetre,
  lightKeysInOrder,
  lightProductName,
  type LightSymbol,
  lightSymbolOf,
  lightTagKey,
  lightTags,
  lightWatts,
  pathLengthOf,
  skyCount,
  stripRuns,
  updatedTagOrder,
} from './lights';
import {
  FLOOR_KINDS,
  floorLabel,
  polygonArea,
} from './flooring';
import { CONNECTION_TYPES } from '../shared/types';
import {
  CONN_HOST_KINDS,
  connDiscipline,
  connGeom,
  type ConnHostKind,
} from './connections';
import { pipeTally } from './pipes';
import {
  connectionLength,
  formatCableLength,
} from './cableLength';
import { pipeTypeName } from '../shared/pipeTypes';
import {
  numberedNames,
  objectName,
  polygonWord,
  wallWord,
} from './naming';
import { trIn } from './i18n';
import { productName } from '../shared/names';
import {
  placedVariant,
  preparationText,
} from './variants';

/** Resolve a translatable string for the drawing's language, falling back to base. */
function tr(base: string, m: TextI18n | undefined, lang: Lang): string {
  const v = m?.[lang];
  return v && v.trim() ? v : base;
}

interface PrepEntry {
  prep: Preparation;
  hostId: string;
  hostName: string;
  /** The variant its placed product uses: an electrical one's text adds its power. */
  variant?: ProductVariant;
}

/** A preparation's text on a schedule, in the drawing's language: an electrical
 *  one's with its product's variant's power and voltage (docs/claude/products.md,
 *  *Product variants*). */
function prepText(e: PrepEntry, lang: Lang): string {
  const label = tr(e.prep.label || '', e.prep.labelI18n, lang);
  return e.prep.type === 'electrical' ? preparationText(label, e.variant, lang) : label;
}

/** Every preparation in the drawing — from parts AND polygons — each paired with
 *  its host id and numbered display name, in a stable order (parts first, then
 *  polygons). This is the single source of truth for preparation numbering, so
 *  the schedule, the connection-number offset, and the table rows all agree. */
function preparationEntries(drawing: Drawing): PrepEntry[] {
  const lang = drawing.language ?? 'it';
  const instNames = numberedNames(drawing.instances, (i) => productName(i, lang));
  const polys = drawing.polygons || [];
  const polyNames = numberedNames(polys, (p) => objectName(p.name, polygonWord(p), lang, trIn));
  const out: PrepEntry[] = [];
  for (const inst of drawing.instances) {
    const variant = placedVariant(drawing, inst);
    for (const p of inst.preparations || []) out.push({ prep: p, hostId: inst.id, hostName: instNames.get(inst.id) ?? productName(inst, lang), variant });
  }
  for (const poly of polys) for (const p of poly.preparations || []) out.push({ prep: p, hostId: poly.id, hostName: polyNames.get(poly.id) ?? trIn(polygonWord(poly), lang) });
  return out;
}

export interface ScheduleRow {
  id: string;
  num: string;
  /** Primary text (connection endpoints, or the part a preparation belongs to). */
  text: string;
  label: string;
}
export interface ScheduleGroup {
  type: ConnectionType;
  rows: ScheduleRow[];
}

const ORDER: readonly ConnectionType[] = CONNECTION_TYPES;
export const TYPE_PREFIX: Record<ConnectionType, string> = {
  electrical: 'E',
  plumbing: 'P',
  mechanical: 'M',
  drainage: 'D',
};
export const TYPE_LABEL: Record<ConnectionType, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  mechanical: 'Mechanical',
  drainage: 'Drainage',
};

/** Discipline labels per drawing language (falls back to English). */
const TYPE_LABEL_I18N: Record<Lang, Record<ConnectionType, string>> = {
  en: TYPE_LABEL,
  it: { electrical: 'Elettrico', plumbing: 'Idraulico', mechanical: 'Meccanico', drainage: 'Scarico' },
  fr: { electrical: 'Électricité', plumbing: 'Plomberie', mechanical: 'Mécanique', drainage: 'Évacuation' },
};

/** Discipline label (Electrical / Elettrico / Électricité …) for the given language, as the UI names it. */
export function typeLabel(type: ConnectionType, lang: Lang): string {
  return (TYPE_LABEL_I18N[lang] ?? TYPE_LABEL)[type];
}

/** Fixed schedule UI strings (titles, column headers) per drawing language. */
type FloorLabels = Record<FloorKind, string>;
type Installer = NonNullable<Connection['installedBy']>;
type LightKindLabels = { strip: string; spot: string; fibre: string; sky: string; cove: string; channel: string };
export const SCHEDULE_STRINGS: Record<Lang, {
  connectionTitle: string; preparationTitle: string; flooringTitle: string;
  connectionsHeading: string; preparationsHeading: string;
  no: string; discipline: string; connectionCol: string; partCol: string; description: string;
  startPart: string; endPart: string;
  typeCol: string; areaCol: string; drainCol: string; drainNone: string; floorKinds: FloorLabels;
  lightingTitle: string; tagCol: string; symbolCol: string; lightCol: string; roomCol: string; qtyCol: string; powerCol: string;
  total: string; pcs: string; kit: string; kits: string; lightKinds: LightKindLabels; stripPlaces: Record<StripPos, string>;
  pipeTitle: string; pipeCol: string; itemCol: string; straightItem: string; hoseItem: string; bendItem: string;
  lengthCol: string; installedByCol: string; installers: Record<Installer, string>;
}> = {
  en: {
    connectionTitle: 'CONNECTION SCHEDULE', preparationTitle: 'PREPARATION SCHEDULE', flooringTitle: 'FLOORING SCHEDULE',
    connectionsHeading: 'Connections', preparationsHeading: 'Preparations',
    no: 'No.', discipline: 'Discipline', connectionCol: 'Connection', partCol: 'Part', description: 'Description',
    startPart: 'From', endPart: 'To',
    typeCol: 'Type', areaCol: 'Area', drainCol: 'Drain', drainNone: '—',
    floorKinds: { 'tiled-flat': 'Flat tiled floor', 'tiled-slope': 'Sloping tiled floor', 'screed-flat': 'Flat screed', 'screed-slope': 'Sloping screed' },
    lightingTitle: 'LIGHTING SCHEDULE', tagCol: 'Tag', symbolCol: 'Symbol', lightCol: 'Light', roomCol: 'Room', qtyCol: 'Qty', powerCol: 'Power',
    total: 'Total', pcs: 'pcs', kit: 'kit', kits: 'kits',
    lightKinds: { strip: 'Strip light', spot: 'Spot light', fibre: 'Fibre point', sky: 'Starry sky, {n} fibres', cove: 'Cove light', channel: 'Channel light' },
    stripPlaces: { A1: 'A1 under the front edge', A2: 'A2 at the toe-kick', B: 'B at the back of the seat', C: 'C behind the backrest’s top edge', D: 'D behind the backrest’s bottom edge' },
    pipeTitle: 'PIPE SCHEDULE', pipeCol: 'Pipe', itemCol: 'Item', straightItem: 'Straight pipe', hoseItem: 'Flexible hose', bendItem: '{a}° bend',
    lengthCol: 'Length', installedByCol: 'Installed by', installers: { stenal: 'Stenal', client: 'Client' },
  },
  it: {
    connectionTitle: 'SCHEMA COLLEGAMENTI', preparationTitle: 'SCHEMA PREDISPOSIZIONI', flooringTitle: 'SCHEMA PAVIMENTI',
    connectionsHeading: 'Collegamenti', preparationsHeading: 'Predisposizioni',
    no: 'N.', discipline: 'Disciplina', connectionCol: 'Collegamento', partCol: 'Componente', description: 'Descrizione',
    startPart: 'Da', endPart: 'A',
    typeCol: 'Tipo', areaCol: 'Area', drainCol: 'Scarico', drainNone: '—',
    floorKinds: { 'tiled-flat': 'Pavimento piastrellato piano', 'tiled-slope': 'Pavimento piastrellato in pendenza', 'screed-flat': 'Massetto piano', 'screed-slope': 'Massetto in pendenza' },
    lightingTitle: 'SCHEMA ILLUMINAZIONE', tagCol: 'Sigla', symbolCol: 'Simbolo', lightCol: 'Luce', roomCol: 'Locale', qtyCol: 'Q.tà', powerCol: 'Potenza',
    total: 'Totale', pcs: 'pz', kit: 'kit', kits: 'kit',
    lightKinds: { strip: 'Striscia LED', spot: 'Faretto', fibre: 'Punto in fibra ottica', sky: 'Cielo stellato, {n} fibre', cove: 'Gola luminosa', channel: 'Canalina luminosa' },
    stripPlaces: { A1: 'A1 sotto il bordo frontale', A2: 'A2 allo zoccolo', B: 'B sul retro della seduta', C: 'C dietro il bordo superiore dello schienale', D: 'D dietro il bordo inferiore dello schienale' },
    pipeTitle: 'SCHEMA TUBAZIONI', pipeCol: 'Tubo', itemCol: 'Elemento', straightItem: 'Tubo diritto', hoseItem: 'Tubo flessibile', bendItem: 'Curva {a}°',
    lengthCol: 'Lunghezza', installedByCol: 'Installato da', installers: { stenal: 'Stenal', client: 'Cliente' },
  },
  fr: {
    connectionTitle: 'SCHÉMA DES RACCORDEMENTS', preparationTitle: 'SCHÉMA DES PRÉPARATIONS', flooringTitle: 'SCHÉMA DES SOLS',
    connectionsHeading: 'Raccordements', preparationsHeading: 'Préparations',
    no: 'N°', discipline: 'Discipline', connectionCol: 'Raccordement', partCol: 'Élément', description: 'Description',
    startPart: 'De', endPart: 'À',
    typeCol: 'Type', areaCol: 'Surface', drainCol: 'Évacuation', drainNone: '—',
    floorKinds: { 'tiled-flat': 'Sol carrelé plat', 'tiled-slope': 'Sol carrelé en pente', 'screed-flat': 'Chape plate', 'screed-slope': 'Chape en pente' },
    lightingTitle: 'SCHÉMA D’ÉCLAIRAGE', tagCol: 'Repère', symbolCol: 'Symbole', lightCol: 'Éclairage', roomCol: 'Pièce', qtyCol: 'Qté', powerCol: 'Puissance',
    total: 'Total', pcs: 'pcs', kit: 'kit', kits: 'kits',
    lightKinds: { strip: 'Ruban LED', spot: 'Spot', fibre: 'Point en fibre optique', sky: 'Ciel étoilé, {n} fibres', cove: 'Gorge lumineuse', channel: 'Rainure lumineuse' },
    stripPlaces: { A1: 'A1 sous le bord avant', A2: 'A2 en pied de plinthe', B: 'B à l’arrière de l’assise', C: 'C derrière le bord supérieur du dossier', D: 'D derrière le bord inférieur du dossier' },
    pipeTitle: 'SCHÉMA DES TUYAUTERIES', pipeCol: 'Tuyau', itemCol: 'Élément', straightItem: 'Tube droit', hoseItem: 'Tuyau flexible', bendItem: 'Coude {a}°',
    lengthCol: 'Longueur', installedByCol: 'Installé par', installers: { stenal: 'Stenal', client: 'Client' },
  },
};

/** One row of the Pipe table, a bill of materials: a pipe type's straight pipe
 *  (a hose's whole length) in metres, or its bends of one angle, counted. */
export interface PipeRow { id: string; type: ConnectionType; name: string; item: string; angle: number; count: number; length: number; qty: string }

/** The Pipe table's rows: per pipe type, in the order the types are first used,
 *  its straight pipe's total length (a hose's along its curve), then its bends
 *  by angle, the widest first (90° and 45° within 6°, any other to the degree). */
export function pipeRows(drawing: Drawing): PipeRow[] {
  const lang = drawing.language ?? 'it', s = scheduleStrings(lang);
  const types = new Map<string, { type: ConnectionType; name: string; hose: boolean; length: number; bends: Map<number, number> }>();
  for (const c of drawing.connections || []) {
    if (!c.pipe) continue;
    const p = connGeom(drawing, c)?.pipe;
    if (!p) continue;
    const t = pipeTally(p.geom), pt = p.type;
    const e = types.get(c.pipe.type) ?? { type: pt?.discipline ?? connDiscipline(c), name: pt ? pipeTypeName(pt, lang) : c.pipe.type, hose: p.flexible, length: 0, bends: new Map<number, number>() };
    e.length += e.hose ? t.length : t.straightLength;
    for (const a of [...Array<number>(t.bends90).fill(90), ...Array<number>(t.bends45).fill(45), ...t.other]) e.bends.set(a, (e.bends.get(a) ?? 0) + 1);
    types.set(c.pipe.type, e);
  }
  const rows: PipeRow[] = [];
  for (const [id, e] of types) {
    const base = { type: e.type, name: e.name };
    if (e.length >= 1) rows.push({ ...base, id: `${id}|straight`, item: e.hose ? s.hoseItem : s.straightItem, angle: 0, count: 0, length: e.length, qty: `${(e.length / 1000).toFixed(2)} m` });
    for (const [angle, count] of [...e.bends].sort((a, b) => b[0] - a[0])) {
      rows.push({ ...base, id: `${id}|bend:${angle}`, item: s.bendItem.replace('{a}', String(angle)), angle, count, length: 0, qty: `${count} ${s.pcs}` });
    }
  }
  return rows;
}

/** A Pipe table row's text: its pipe, the item and how much of it. */
export const pipeRowCells = (r: PipeRow): string[] => [r.name, r.item, r.qty];

/** Schedule UI strings for the given language (falls back to English). */
export function scheduleStrings(lang: Lang): (typeof SCHEDULE_STRINGS)['en'] {
  return SCHEDULE_STRINGS[lang] ?? SCHEDULE_STRINGS.en;
}

function group<T>(
  items: T[],
  typeOf: (t: T) => ConnectionType,
  rowOf: (t: T, num: string) => ScheduleRow,
  // Per-discipline number to start after (so a second schedule can continue the
  // same sequence instead of restarting at 1 and colliding). Default 0.
  startAfter: (type: ConnectionType) => number = () => 0
): ScheduleGroup[] {
  const groups: ScheduleGroup[] = [];
  for (const type of ORDER) {
    const ofType = items.filter((t) => typeOf(t) === type);
    if (ofType.length === 0) continue;
    const base = startAfter(type);
    groups.push({ type, rows: ofType.map((t, i) => rowOf(t, TYPE_PREFIX[type] + (base + i + 1))) });
  }
  return groups;
}

/** What a connection's end is called in the drawing's language, as the schedule,
 *  its copy for Word and the connection table all name it: its object's numbered
 *  name (a part, a polygon, a wall or line, a text box's first line, a floor),
 *  '—' for a free end. */
export function connEndNamer(drawing: Drawing, lang: Lang): (id: string) => string {
  const maps = CONN_HOST_KINDS.map((k) => END_NAMES[k](drawing, lang));
  return (id) => {
    if (!id) return '—';
    for (const m of maps) { const n = m.get(id); if (n) return n; }
    return '?';
  };
}

/** Each host kind's objects numbered by name in `lang` (naming.ts): what a
 *  connection's end on one is called. A new host kind does not compile until it
 *  is named here. */
const END_NAMES: { [K in ConnHostKind]: (d: Drawing, lang: Lang) => Map<string, string> } = {
  text: (d, lang) => numberedNames(d.texts || [], (t) => (t.text || '').split('\n')[0].trim().slice(0, 28) || trIn('Text', lang)),
  instance: (d, lang) => numberedNames(d.instances, (i) => productName(i, lang)),
  polygon: (d, lang) => numberedNames(d.polygons || [], (p) => objectName(p.name, polygonWord(p), lang, trIn)),
  wall: (d, lang) => numberedNames(d.walls || [], (w) => objectName(w.name, wallWord(w), lang, trIn)),
  flooring: (d, lang) => numberedNames(d.floorings || [], (f) => objectName(f.name, floorLabel(f.kind), lang, trIn)),
};

/** Connections grouped by type and numbered (E1, P1, M1 …). */
export function connectionSchedule(drawing: Drawing): ScheduleGroup[] {
  const lang = drawing.language ?? 'it';
  const nameOf = connEndNamer(drawing, lang);
  // Connections continue the same per-discipline sequence after the preparations,
  // so no preparation and connection ever share a number (E1/E2 preparations →
  // E3/E4… connections).
  const allPreps = preparationEntries(drawing);
  const preparationsOfType = (type: ConnectionType) =>
    allPreps.filter((e) => e.prep.type === type).length;
  return group(
    drawing.connections,
    (c) => connDiscipline(c),
    (c, num) => ({
      id: c.id,
      num,
      text: `${nameOf(c.from)} → ${nameOf(c.to)}${c.lines > 1 ? ` (×${c.lines})` : ''}`,
      label: tr(c.label || '', c.labelI18n, lang),
    }),
    preparationsOfType
  );
}

/** Preparations across all parts, grouped by type and numbered. */
export function preparationSchedule(drawing: Drawing): ScheduleGroup[] {
  const lang = drawing.language ?? 'it';
  const preps = preparationEntries(drawing);
  // Preparations are numbered first (E1, E2…); connections continue the sequence.
  return group(
    preps,
    (x) => x.prep.type,
    (x, num) => ({ id: x.prep.id, num, text: x.hostName, label: prepText(x, lang) })
  );
}

/** One connection row for the in-drawing schedule table: the two endpoint part
 *  names resolved and numbered, the discipline number, and the description. */
export interface ConnectionRow {
  id: string;
  num: string;
  type: ConnectionType;
  startId: string;
  endId: string;
  startName: string;
  endName: string;
  description: string;
  /** Its cable's length (`connectionLength`, mm) and as the table shows it ("10 m"). */
  length: number;
  lengthText: string;
  installedBy: Installer;
  /** Who lays it, in the drawing's language. */
  installedByText: string;
}

/** Flat, discipline-ordered connection rows (same numbering as the schedule),
 *  with the start/end part names split out for the table's columns. */
export function connectionRows(drawing: Drawing): ConnectionRow[] {
  const lang = drawing.language ?? 'it', s = scheduleStrings(lang);
  const nameOf = connEndNamer(drawing, lang);
  const numById = scheduleNumbers(connectionSchedule(drawing));
  const rows: ConnectionRow[] = [];
  for (const type of ORDER) {
    for (const c of drawing.connections.filter((x) => connDiscipline(x) === type)) {
      const length = connectionLength(drawing, c), installedBy: Installer = c.installedBy ?? 'stenal';
      rows.push({
        id: c.id,
        num: numById.get(c.id) ?? '',
        type,
        startId: c.from,
        endId: c.to,
        startName: nameOf(c.from),
        endName: nameOf(c.to),
        description: tr(c.label || '', c.labelI18n, lang),
        length,
        lengthText: formatCableLength(length),
        installedBy,
        installedByText: s.installers[installedBy],
      });
    }
  }
  return rows;
}

/** One preparation row for the in-drawing schedule table: the owning part's
 *  name (numbered) and the preparation's discipline number + description. */
export interface PreparationRow {
  id: string;
  num: string;
  type: ConnectionType;
  partId: string;
  partName: string;
  description: string;
}

/** Flat, discipline-ordered preparation rows (same numbering as the schedule). */
export function preparationRows(drawing: Drawing): PreparationRow[] {
  const lang = drawing.language ?? 'it';
  const numById = scheduleNumbers(preparationSchedule(drawing));
  const entries = preparationEntries(drawing);
  const rows: PreparationRow[] = [];
  for (const type of ORDER) {
    for (const e of entries.filter((x) => x.prep.type === type)) {
      rows.push({
        id: e.prep.id,
        num: numById.get(e.prep.id) ?? '',
        type,
        partId: e.hostId,
        partName: e.hostName,
        description: prepText(e, lang),
      });
    }
  }
  return rows;
}

/** One flooring row: its tag (F1, F2, …), the floor-type label, the area (m²) and
 *  the drain's name (or a dash for a flat floor). */
export interface FlooringRow {
  id: string;       // the floor kind — the dedup key
  kind: FloorKind;  // drives the hatch swatch drawn in place of a code
  typeLabel: string;
  area: string;     // total m² of all floors of this type
  drain: string;    // the drain(s) used by this type, or a dash
}

/** ONE row per floor TYPE (kinds present in the drawing, in canonical order):
 *  areas of same-type floors are summed and their drains merged, so the schedule
 *  is a legend keyed by the hatch — there is no per-floor code. */
export function flooringRows(drawing: Drawing): FlooringRow[] {
  const s = SCHEDULE_STRINGS[drawing.language ?? 'it'] ?? SCHEDULE_STRINGS.en;
  const byKind = new Map<FloorKind, { area: number; drains: Set<string> }>();
  for (const f of drawing.floorings ?? []) {
    const e = byKind.get(f.kind) ?? { area: 0, drains: new Set<string>() };
    e.area += polygonArea(f.points);
    const d = f.drain?.name ? productName({ name: f.drain.name, nameI18n: f.drain.nameI18n }, drawing.language ?? 'it') : '';
    if (d) e.drains.add(d);
    byKind.set(f.kind, e);
  }
  const rows: FlooringRow[] = [];
  for (const kind of FLOOR_KINDS) {
    const e = byKind.get(kind);
    if (!e) continue;
    rows.push({
      id: kind,
      kind,
      typeLabel: s.floorKinds[kind],
      area: `${(e.area / 1e6).toFixed(2)} m²`,
      drain: e.drains.size ? [...e.drains].join(', ') : s.drainNone,
    });
  }
  return rows;
}


/** One row of the Lighting table: a tag — one product in one place — in one
 *  room, how much of it there (metres, pieces or kits) and its power. */
export interface LightingRow { id: string; tag: string; symbol: LightSymbol; light: string; room: string; qty: string; power: string; watts: number }

/** The Lighting table's rows, a tag's in each room apart (by tag, then room in
 *  drawing order): a strip's, the cove's and a channel's length times their
 *  product's W/m, a spot's count and a fibre kit's (a sky, a fibre point) times
 *  their product's watts — a kit's product is its projector. */
export function lightingRows(drawing: Drawing): LightingRow[] {
  const lang = drawing.language ?? 'it', s = scheduleStrings(lang);
  const tags = lightTags(updatedTagOrder(drawing.lightTagOrder, lightKeysInOrder(drawing.instances)));
  const names = numberedNames(drawing.instances, (i) => productName(i, lang));
  const acc = new Map<string, { key: string; first: RoomLight; room: string; mm: number; n: number }>();
  const add = (inst: ProductInstance, light: RoomLight, mm: number, n: number): void => {
    const key = lightTagKey(light), id = `${key}@${inst.id}`;
    const e = acc.get(id) ?? { key, first: light, room: names.get(inst.id) ?? inst.name, mm: 0, n: 0 };
    e.mm += mm; e.n += n;
    acc.set(id, e);
  };
  for (const inst of drawing.instances) {
    const lights = inst.lights ?? [];
    if (!lights.length) continue;
    const byId = new Map(lights.map((l) => [l.id, l]));
    for (const run of stripRuns(inst)) { const l = byId.get(run.ids[0]); if (l) add(inst, l, run.length, 0); }
    for (const l of lights) {
      if (l.kind === 'cove') add(inst, l, (coveGeometry(inst)?.runs ?? []).reduce((m, r) => m + r.length, 0), 0);
      else if (l.kind === 'channel') add(inst, l, pathLengthOf(l.pts), 0);
      else if (l.kind === 'spot' || l.kind === 'sky') add(inst, l, 0, 1);
    }
  }
  const rows = [...acc].map(([id, e]): LightingRow => {
    const l = e.first, t = catalogueLightProduct(l.productId ?? defaultLightProductId(l));
    const product = lightProductName(l, t, lang);
    const what = l.kind === 'strip' ? `${s.lightKinds.strip}, ${s.stripPlaces[l.pos]}`
      : l.kind === 'sky' ? s.lightKinds.sky.replace('{n}', String(skyCount(l)))
        : l.kind === 'spot' ? (l.fibre ? s.lightKinds.fibre : s.lightKinds.spot) : s.lightKinds[l.kind];
    const metres = lightByTheMetre(l);
    const kit = l.kind === 'sky' || (l.kind === 'spot' && !!l.fibre);
    const watts = lightWatts(l, metres ? e.mm : e.n);
    const qty = metres ? `${(e.mm / 1000).toFixed(2)} m` : `${e.n} ${kit ? (e.n === 1 ? s.kit : s.kits) : s.pcs}`;
    return { id, tag: tags.get(e.key) ?? '', symbol: lightSymbolOf(l), light: product ? `${what} — ${product}` : what, room: e.room, qty, power: `${Math.round(watts)} W`, watts };
  });
  // A stable sort: a tag's rooms stay in drawing order, as they were added.
  return rows.sort((a, b) => Number(a.tag.slice(1)) - Number(b.tag.slice(1)));
}

/** The legend's rows (a DXF has no tables, and no room column): each tag once. */
export const lightingLegendRows = (rows: LightingRow[]): LightingRow[] => rows.filter((r, i) => rows.findIndex((o) => o.tag === r.tag) === i);

/** Map each row id (connection or preparation) to its schedule number. */
export function scheduleNumbers(groups: ScheduleGroup[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const g of groups) for (const r of g.rows) m.set(r.id, r.num);
  return m;
}
